// Module Import
//---------------------------------
import React, { useRef, useEffect } from 'react'
import styled from 'styled-components'
import ReCAPTCHA from 'react-google-recaptcha'
import Field from '../../atoms/fields/field'
import TypographyAtom from '../../atoms/typography/typography'
import 'react-datepicker/dist/react-datepicker.css'
import TextareaField from '../../atoms/fields/textarea'
import ValidationWrapper from '../../atoms/fields/ValidationWrapper'
import PhoneFieldWithCountry from '../../atoms/fields/PhoneField/PhoneFieldWithCountry'
import { brandError } from '../../../assets/consts/color'

// Component Import
//---------------------------------
const Form = styled.form`
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  margin-bottom: 24px;
`

const BodyWrapper = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: space-between;

  span,
  label {
    line-height: 115%;
  }

  @media only screen and (max-width: 48em) {
    gap: 12px;
  }
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;

  & > div {
    flex: 1;
  }

  width: 100%;

  @media only screen and (min-width: 48em) and (max-width: 1450px) {
    width: 100% !important;
  }

  @media only screen and (min-width: 48em) {
    flex: ${(props) => (props?.half ? '1' : 'auto')};
  }
`

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  line-height: 0;

  position: relative;
  & > div:first-child:not(.input-wrapper) {
    width: 100%;
  }

  .input-wrapper {
    width: 100%;

    label {
      position: relative;
    }

    label[aria-required='true']::after {
      position: absolute;
      top: 0;
      right: -10px;
      content: '*';
      color: ${brandError};
    }

    .error {
      font-size: 16px;
      color: ${brandError};
    }
  }
`

// Component Export
//---------------------------------
export default function EnquireForm({
  onSubmit,
  formValues,
  onChange,
  errors,
  onChangeCaptcha,
  setCaptchaRef,
  setFormRef,
  shouldApplyCourse,
}) {
  const formRef = useRef(null)
  const recaptcha = useRef(null)
  const { firstName, lastName, email, phone, clientQuery } = formValues

  const handleCaptchaChange = (captchaValue) => {
    onChangeCaptcha(captchaValue)
  }

  const handlePhoneChange = ({ name, value }) => onChange({ target: { name, value } })

  useEffect(() => {
    if (setFormRef) setFormRef(formRef)
    if (setCaptchaRef) setCaptchaRef(recaptcha)
  }, [formRef, recaptcha])

  return (
    <>
      <Form id="rentalForm" ref={formRef} onSubmit={onSubmit}>
        <BodyWrapper>
          <FieldWrapper half>
            <FieldItem>
              <ValidationWrapper errorMessage={errors.firstName}>
                <Field
                  labelText="First Name"
                  labelFor="firstName"
                  placeholderText="Enter your first name"
                  fieldType="text"
                  fieldId="firstName"
                  fieldName="firstName"
                  value={firstName}
                  fullWidth
                  onChange={onChange}
                />
              </ValidationWrapper>
            </FieldItem>
          </FieldWrapper>
          <FieldWrapper half>
            <FieldItem>
              <ValidationWrapper errorMessage={errors.lastName}>
                <Field
                  labelText="Last Name"
                  labelFor="lastName"
                  placeholderText="Enter your last name"
                  fieldType="text"
                  fieldId="lastName"
                  fieldName="lastName"
                  value={lastName}
                  fullWidth
                  onChange={onChange}
                />
              </ValidationWrapper>
            </FieldItem>
          </FieldWrapper>
          <FieldWrapper>
            <FieldItem>
              <ValidationWrapper errorMessage={errors.email}>
                <Field
                  labelText="Email Address"
                  labelFor="email"
                  placeholderText="Enter your email"
                  fieldType="text"
                  fieldId="email"
                  fieldName="email"
                  autocomplete="email"
                  value={email}
                  fullWidth
                  onChange={onChange}
                />
              </ValidationWrapper>
            </FieldItem>
          </FieldWrapper>
          <FieldWrapper>
            <FieldItem>
              <ValidationWrapper errorMessage={errors.phone}>
                <PhoneFieldWithCountry
                  value={phone}
                  placeholder="Enter your phone"
                  fieldName="phone"
                  onChange={handlePhoneChange}
                />
              </ValidationWrapper>
            </FieldItem>
          </FieldWrapper>
          {!shouldApplyCourse && (
            <FieldWrapper>
              <FieldItem>
                <ValidationWrapper errorMessage={errors.clientQuery}>
                  <TextareaField
                    labelText="What queries did you have about this product?"
                    labelFor="clientQuery"
                    placeholderText="Enter what queries did you have about this product"
                    fieldType="text"
                    fieldId="clientQuery"
                    fieldName="clientQuery"
                    value={clientQuery}
                    onChange={onChange}
                  />
                </ValidationWrapper>
              </FieldItem>
            </FieldWrapper>
          )}
          <ReCAPTCHA
            sitekey={process.env.GATSBY_SITE_KEY}
            onChange={handleCaptchaChange}
            ref={recaptcha}
          />
        </BodyWrapper>
      </Form>
    </>
  )
}

EnquireForm.propTypes = {}
